import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { PROBLEM_REPORT_OPTIONS, PROBLEM_REPORTS_ENABLED } from '@nsc/smartteams-shared/db/SmartTeamsCustomer.js';
import { html, nothing } from 'lit';

/**
 * Per-customer opt-in switches for the smartteams-app "Report a problem" buttons.
 * Renders one toggle per option, checked = button enabled. Persists via `onChange`
 * with the ENABLED keys (the storage format on SmartTeamsCustomer), so an empty
 * array / missing field means "nothing available". (Request cancellation isn't listed
 * here — it's always available to the FE and not configurable.)
 *
 * When `PROBLEM_REPORTS_ENABLED` is false the whole feature is off product-wide: the toggles
 * render read-only and unchecked, and a notice explains they can't be enabled. The server ignores
 * the stored values in that state regardless, so this is purely so admins aren't misled.
 */
export const ProblemReportToggles = stateful(
  ({ value, onChange }: { value: string[]; onChange: (enabledKeys: string[]) => void }) => {
    const [enabled, setEnabled] = useState<string[]>(value);

    const toggle = (key: string, on: boolean) => {
      if (!PROBLEM_REPORTS_ENABLED) return;
      const next = on ? [...enabled, key] : enabled.filter(k => k !== key);
      setEnabled(next);
      onChange(next);
    };

    return html`
      <div class="flex flex-col gap-2">
        <p class="text-sm text-base-content/60">
          Which "Report a problem" buttons field engineers see on this customer's tickets. All are off by default;
          enable the ones this customer should have. Enabled ones are also the only ones accepted server-side.
        </p>
        ${!PROBLEM_REPORTS_ENABLED
          ? html`
              <div class="alert alert-info text-sm py-2">
                <span class="icon-[mdi--information-outline] text-base"></span>
                <span
                  >Report-a-problem buttons are currently disabled and cannot be enabled. Only "Request cancellation"
                  remains available to field engineers.</span
                >
              </div>
            `
          : nothing}
        ${PROBLEM_REPORT_OPTIONS.map(option => {
          const checked = PROBLEM_REPORTS_ENABLED && enabled.includes(option.key);
          return html`
            <label
              class="flex items-center justify-between gap-3 py-1 ${PROBLEM_REPORTS_ENABLED
                ? 'cursor-pointer'
                : 'opacity-60'}"
            >
              <span class="text-sm">${option.label}</span>
              <input
                type="checkbox"
                class="toggle toggle-primary shrink-0"
                ?checked=${checked}
                ?disabled=${!PROBLEM_REPORTS_ENABLED}
                @change=${(e: Event) => toggle(option.key, (e.target as HTMLInputElement).checked)}
              />
            </label>
          `;
        })}
      </div>
    `;
  },
);
